import React, { createContext, useCallback } from 'react';
import { listTriggerLogs } from '../../../../services';
import { useBaseContext } from './useBaseContext';

const initialState = {
  data: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  },
  filters: {
    search: '',
    action: '',
    lastUpdate: '',
    modifiedBy: ''
  },
  mode: 'table' 
};

const filterLogs = (logs, filters) => {
  let filtered = [...logs];

  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(log => 
      log.name.toLowerCase().includes(searchLower) ||
      log.details.toLowerCase().includes(searchLower) ||
      log.modifiedBy.toLowerCase().includes(searchLower)
    );
  }

  if (filters.action && filters.action.length > 0) {
    filtered = filtered.filter(log => 
      filters.action.includes(log.action)
    );
  }
  
  if (filters.modifiedBy && filters.modifiedBy.length > 0) {
    filtered = filtered.filter(log => 
      filters.modifiedBy.includes(log.modifiedBy.toLowerCase().replace(' ', '_'))
    );
  }

  if (filters.lastUpdate) {
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const thisYear = new Date(now.getFullYear(), 0, 1);

    filtered = filtered.filter(log => {
      const lastUpdated = new Date(log.dateOfChange);
      
      return (filters.lastUpdate === 'last_7_days' && lastUpdated >= sevenDaysAgo) ||
             (filters.lastUpdate === 'last_30_days' && lastUpdated >= thirtyDaysAgo) ||
             (filters.lastUpdate === 'this_year' && lastUpdated >= thisYear) ||
             filters.lastUpdate === 'all';
    });
  }

  return filtered;
};

const TriggersLogsContext = createContext();

export const TriggersLogsProvider = ({ children }) => {
  // Use the base hook with logs-specific configuration
  const baseContext = useBaseContext(
    initialState,
    listTriggerLogs,
    filterLogs,
    'logs'
  );

  const { setPage, setItemsPerPage, reloadData } = baseContext;

  // Enhanced setPage and setItemsPerPage that reload data
  const setPageWithReload = useCallback((page) => {
    setPage(page);
    reloadData();
  }, [setPage, reloadData]);

  const setItemsPerPageWithReload = useCallback((itemsPerPage) => {
    setItemsPerPage(itemsPerPage);
    reloadData();
  }, [setItemsPerPage, reloadData]);

  // Context value combining base functionality with logs-specific aliases
  const contextValue = {
    ...baseContext,
    // Alias for backward compatibility
    logs: baseContext.data,
    filteredLogs: baseContext.filteredItems,
    loadTriggerLogs: baseContext.loadItems,
    // Enhanced pagination functions
    setPage: setPageWithReload,
    setItemsPerPage: setItemsPerPageWithReload
  };

  return (
    <TriggersLogsContext.Provider value={contextValue}>
      {children}
    </TriggersLogsContext.Provider>
  );
};

export const useTriggersLogs = () => {
  const context = React.useContext(TriggersLogsContext);
  if (!context) {
    throw new Error('useTriggersLogs must be used within a TriggersLogsProvider');
  }
  return context;
}; 